import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { caseStudies } from "../data/mockData";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, itemListSchema } from "../lib/seo";

const CaseStudiesPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToCaseStudies = (e) => {
    e.preventDefault();
    navigate("/");
    // Wait for Home route to mount + layout to settle, then scroll.
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById("case-studies");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      } else if (attempt < 20) {
        setTimeout(() => tryScroll(attempt + 1), 50);
      }
    };
    setTimeout(() => tryScroll(0), 50);
  };

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies")}
        schema={[
          itemListSchema("/case-studies", caseStudies.map((c) => ({ path: c.link, name: c.title }))),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
          ]),
        ]}
      />
      {/* Page Header */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/"
            onClick={handleBackToCaseStudies}
            className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <p className="text-base md:text-lg font-semibold text-[#009688] uppercase tracking-wide mb-4">
            Case Studies
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Proof in The Wristband
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Real results from real clients. See how WristCoin powers seamless cashless experiences across venues, resorts, and events.
          </p>
        </div>
      </section>

      {/* Grid of Case Studies */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card
                key={index}
                className="bg-[#37464F]/50 border-gray-800 hover:border-[#009688]/50 transition-all duration-300 overflow-hidden group flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101F27]/80 to-transparent"></div>
                </div>
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="text-xs text-[#009688] uppercase font-semibold tracking-wide mb-2">
                    {study.location}
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">{study.title}</h2>
                  <p className="text-gray-400 text-sm mb-3">
                    <span className="font-semibold text-gray-300">Challenge: </span>
                    {study.challenge}
                  </p>
                  <p className="text-gray-400 text-sm mb-6">
                    <span className="font-semibold text-gray-300">Result: </span>
                    {study.result}
                  </p>
                  <div className="mt-auto">
                    {study.link ? (
                      <Link
                        to={study.link}
                        className="text-[#009688] text-sm font-semibold hover:underline inline-flex items-center group/cta"
                      >
                        Read Full Case Study
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <span className="text-gray-500 text-sm italic">Coming soon</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
